/**
 * Central registry of route surfaces that the admin access editor can grant
 * via per-user `extraAccess`. Each surface is identified by a route-path
 * prefix; `ProtectedRoute` admits when role matches OR `pathname.startsWith`
 * any prefix in the user's `extraAccess` array.
 *
 * The `rolesByDefault` field is documentation — it should mirror the
 * `allowedRoles` declared in [`App.tsx`](../App.tsx) for the matching route.
 * The admin UI uses it to render a read-only matrix view ("which roles
 * already have this by default?") next to the override checkboxes.
 *
 * Keeping this list in sync with `App.tsx` is the team's responsibility. The
 * cleanup-backlog tracks a future consolidation that would derive both from
 * a single source (see backlog #7 — role-cohort dedup).
 */

import type { Role } from '../store/useAuthStore';

export interface AccessSurface {
  /** Route-path prefix. Matched via `pathname.startsWith(key)`. */
  key: string;
  /** Human-readable label shown in the admin editor. */
  label: string;
  /** Roles that already have this surface by default (mirrors `App.tsx`). */
  rolesByDefault: Role[];
  /** One-line description for the admin tooltip. */
  description?: string;
}

export const ACCESS_SURFACES: AccessSurface[] = [
  {
    key: '/staff/home',
    label: 'Dashboard',
    rolesByDefault: [
      'ADMIN',
      'STAFF',
      'SALES',
      'MEASURER',
      'FINANCE',
      'FINANCE_MANAGER',
      'PURCHASER',
    ],
    description: 'Universal landing dashboard.',
  },
  {
    key: '/staff/payments',
    label: 'Payments view',
    rolesByDefault: ['ADMIN', 'FINANCE', 'FINANCE_MANAGER'],
    description: 'Cross-quote reconciliation.',
  },
  {
    key: '/staff/quotes',
    label: 'Quotations dashboard',
    rolesByDefault: ['ADMIN', 'SALES', 'FINANCE', 'FINANCE_MANAGER', 'PURCHASER'],
    description: 'List of all saved quotes.',
  },
  {
    key: '/staff/orders',
    label: 'Orders',
    rolesByDefault: ['ADMIN', 'SALES', 'MEASURER', 'FINANCE', 'FINANCE_MANAGER', 'PURCHASER'],
    description: 'Orders list + detail.',
  },
  {
    key: '/staff/customers',
    label: 'Customers',
    rolesByDefault: ['ADMIN', 'SALES', 'FINANCE', 'FINANCE_MANAGER', 'PURCHASER'],
    description: 'Customer master data.',
  },
  {
    key: '/staff/vendors',
    label: 'Vendors',
    rolesByDefault: ['ADMIN', 'SALES', 'FINANCE', 'FINANCE_MANAGER', 'PURCHASER'],
    description: 'Vendor master data.',
  },
  {
    key: '/staff/requisitions',
    label: 'Purchase requisitions',
    rolesByDefault: ['ADMIN', 'SALES', 'FINANCE', 'FINANCE_MANAGER', 'PURCHASER'],
    description: 'PR list + workflow.',
  },
  {
    key: '/staff/purchase-orders',
    label: 'Purchase orders',
    rolesByDefault: ['ADMIN', 'SALES', 'FINANCE', 'FINANCE_MANAGER', 'PURCHASER'],
    description: 'PO list + workflow.',
  },
  {
    key: '/staff/schedule',
    label: 'Measurement schedule',
    rolesByDefault: ['ADMIN', 'SALES', 'MEASURER'],
    description: '5-day route grid for the measurement crew.',
  },
  {
    key: '/staff/deliveries',
    label: 'Deliveries',
    rolesByDefault: ['ADMIN', 'SALES'],
    description: 'Dispatcher entry + route planner.',
  },
  {
    key: '/staff/claims',
    label: 'Claims (mine)',
    rolesByDefault: [
      'ADMIN',
      'STAFF',
      'SALES',
      'MEASURER',
      'FINANCE',
      'FINANCE_MANAGER',
      'PURCHASER',
    ],
    description: 'Submit + view own claims. Universal access.',
  },
  {
    key: '/staff/claims/finance',
    label: 'Claims — finance queue',
    rolesByDefault: ['ADMIN', 'FINANCE', 'FINANCE_MANAGER'],
    description: 'Check / explain / approve claim worklist.',
  },
  {
    key: '/staff/claims/export',
    label: 'Claims — GST F5 export',
    rolesByDefault: ['ADMIN', 'FINANCE', 'FINANCE_MANAGER'],
    description: 'Quarterly GST input tax export for IRAS F5 filing.',
  },
  {
    key: '/staff/cash-float',
    label: 'Cash float',
    rolesByDefault: ['ADMIN', 'PURCHASER', 'FINANCE', 'FINANCE_MANAGER'],
    description:
      'Pre-funded ledgers held by staff for China-supplier prepayments. PURCHASER sees own ledgers; finance / admin see all.',
  },
  {
    key: '/staff/admin',
    label: 'Admin panel',
    rolesByDefault: ['ADMIN'],
    description: 'Catalog editor, staff manager, system settings.',
  },
];

/**
 * Does any prefix in `extraAccess` match this pathname?
 *
 * Empty / undefined prefixes are skipped defensively so a corrupted user doc
 * (e.g. a literal empty string saved into the array) can't accidentally grant
 * everything. Matches by `startsWith` — the registry's keys are route
 * prefixes, e.g. `/staff/payments` covers `/staff/payments/anything`.
 */
export const isPathInExtraAccess = (extraAccess: string[], pathname: string): boolean =>
  extraAccess.some((prefix) => prefix && pathname.startsWith(prefix));
